import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Confirm } from 'notiflix/build/notiflix-confirm-aio'
import { Report } from 'notiflix/build/notiflix-report-aio'
import { FaChevronDown, FaChevronRight, FaDownload, FaSlidersH } from 'react-icons/fa'
import api from '../../../helpers/apiRequester'
import { BASE_URL, csvCell, downloadCsvFile, formatDateTime } from './shared'

export type GateBrand = 'fairclaim' | 'ffj'

export interface GateRule {
    key: string
    label: string
    description?: string
    group: string
    enabled: boolean
    params: Record<string, unknown> | null
    updatedAt?: string | null
    updatedBy?: string | null
}

interface GateSnapshot {
    success: boolean
    rules: GateRule[]
    updatedAt?: string | null
    updatedBy?: string | null
}

interface GateControlsProps {
    brand: GateBrand
    brandLabel: string
    styles: Record<string, string>
}

const cloneRules = (rules: GateRule[]): GateRule[] =>
    rules.map((rule) => ({
        ...rule,
        params: rule.params == null ? null : JSON.parse(JSON.stringify(rule.params)),
    }))

export const serialiseRules = (rules: GateRule[]): string =>
    JSON.stringify(rules.map(({ key, enabled, params }) => ({ key, enabled, params: params ?? null })))

const csvRow = (cells: (string | number | null | undefined)[]): string => cells.map(csvCell).join(',')

export const formatGateParams = (params: Record<string, unknown> | null): string => {
    if (!params || Object.keys(params).length === 0) return ''
    return Object.entries(params)
        .map(([name, value]) => {
            if (value !== null && typeof value === 'object') return `${name}: ${JSON.stringify(value)}`
            if (typeof value === 'boolean') return `${name}: ${value ? 'yes' : 'no'}`
            return `${name}: ${value == null ? '' : String(value)}`
        })
        .join('; ')
}

export const buildGateRulesCsv = ({
    brandLabel,
    exportedAt,
    updatedAt,
    updatedBy,
    rules,
}: {
    brandLabel: string
    exportedAt: string
    updatedAt?: string | null
    updatedBy?: string | null
    rules: GateRule[]
}): string => {
    const lines = [
        csvRow([`${brandLabel} gate controls`]),
        csvRow(['Exported at', formatDateTime(exportedAt)]),
        csvRow(['Last updated', formatDateTime(updatedAt)]),
        csvRow(['Last updated by', updatedBy || 'Unknown']),
        csvRow(['Rule count', rules.length]),
        '',
        csvRow([
            'Group',
            'Gate',
            'Description',
            'Key',
            'Status',
            'Configuration',
            'Rule updated at',
            'Rule updated by',
        ]),
        ...rules.map((rule) =>
            csvRow([
                rule.group || 'Other',
                rule.label,
                rule.description ?? '',
                rule.key,
                rule.enabled ? 'Enabled' : 'Disabled',
                formatGateParams(rule.params),
                rule.updatedAt ? formatDateTime(rule.updatedAt) : '',
                rule.updatedBy || '',
            ])
        ),
    ]
    return lines.join('\r\n')
}

export const groupRules = (rules: GateRule[], search: string): [string, GateRule[]][] => {
    const term = search.trim().toLowerCase()
    const grouped = new Map<string, GateRule[]>()
    rules.forEach((rule) => {
        if (
            term &&
            ![rule.label, rule.description, rule.key, rule.group]
                .filter(Boolean)
                .some((value) => String(value).toLowerCase().includes(term))
        ) {
            return
        }
        const group = rule.group || 'Other'
        grouped.set(group, [...(grouped.get(group) ?? []), rule])
    })
    return Array.from(grouped.entries())
}

const errorMessage = (error: any, fallback: string): string =>
    error?.response?.data?.message || error?.response?.data?.error || error?.message || fallback

const ParamInput = ({
    ruleKey,
    name,
    value,
    disabled,
    onChange,
    styles,
}: {
    ruleKey: string
    name: string
    value: unknown
    disabled: boolean
    onChange: (value: unknown) => void
    styles: Record<string, string>
}) => {
    const id = `${ruleKey}-${name}`.replace(/[^a-zA-Z0-9_-]/g, '-')
    if (value !== null && typeof value === 'object') {
        return (
            <JsonParamInput
                id={id}
                name={name}
                value={value}
                disabled={disabled}
                onChange={onChange}
                styles={styles}
            />
        )
    }
    if (typeof value === 'boolean') {
        return (
            <label className={styles.gateParamCheckbox} htmlFor={id}>
                <input
                    id={id}
                    type="checkbox"
                    checked={value}
                    disabled={disabled}
                    onChange={(event) => onChange(event.target.checked)}
                />
                {name}
            </label>
        )
    }

    const isNumber = typeof value === 'number'
    return (
        <label className={styles.gateParam} htmlFor={id}>
            <span>{name}</span>
            <input
                id={id}
                type={isNumber ? 'number' : 'text'}
                value={value == null ? '' : String(value)}
                disabled={disabled}
                onChange={(event) =>
                    onChange(isNumber && event.target.value !== '' ? Number(event.target.value) : event.target.value)
                }
            />
        </label>
    )
}

const JsonParamInput = ({
    id,
    name,
    value,
    disabled,
    onChange,
    styles,
}: {
    id: string
    name: string
    value: unknown
    disabled: boolean
    onChange: (value: unknown) => void
    styles: Record<string, string>
}) => {
    const formatted = JSON.stringify(value)
    const [text, setText] = useState(formatted)
    const [invalid, setInvalid] = useState(false)

    useEffect(() => {
        setText(formatted)
        setInvalid(false)
    }, [formatted])

    return (
        <label className={`${styles.gateParam} ${styles.gateParamJson}`} htmlFor={id}>
            <span>{name} (JSON)</span>
            <textarea
                id={id}
                rows={2}
                value={text}
                disabled={disabled}
                aria-invalid={invalid}
                onChange={(event) => {
                    const next = event.target.value
                    setText(next)
                    try {
                        onChange(JSON.parse(next))
                        setInvalid(false)
                    } catch {
                        setInvalid(true)
                    }
                }}
            />
            {invalid && <small>Enter valid JSON</small>}
        </label>
    )
}

export const GateControls = ({ brand, brandLabel, styles }: GateControlsProps) => {
    const [isAdmin, setIsAdmin] = useState(false)
    const [open, setOpen] = useState(false)
    const [loading, setLoading] = useState(false)
    const [exporting, setExporting] = useState(false)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [snapshot, setSnapshot] = useState<GateSnapshot | null>(null)
    const [rules, setRules] = useState<GateRule[]>([])
    const [search, setSearch] = useState('')
    const [changeNote, setChangeNote] = useState('')
    const [expanded, setExpanded] = useState<Set<string>>(new Set())

    useEffect(() => {
        let active = true
        api('/users-page-auth')
            .then((response) => {
                if (active && response.data?.success !== false) setIsAdmin(true)
            })
            .catch(() => {
                if (active) setIsAdmin(false)
            })
        return () => {
            active = false
        }
    }, [])

    const dirty = useMemo(
        () => Boolean(snapshot) && serialiseRules(rules) !== serialiseRules(snapshot?.rules ?? []),
        [rules, snapshot]
    )
    const groupedRules = useMemo(() => groupRules(rules, search), [rules, search])

    const loadRules = useCallback(async () => {
        setLoading(true)
        setError(null)
        try {
            const response = await api.post(`${BASE_URL}/api/${brand}-gate-rules`, {})
            if (!response.data?.success || !Array.isArray(response.data.rules)) {
                throw new Error(response.data?.message || 'The gate rule response was invalid')
            }
            const next: GateSnapshot = response.data
            const cloned = cloneRules(next.rules)
            setSnapshot({ ...next, rules: cloneRules(next.rules) })
            setRules(cloned)
            setExpanded(new Set(cloned.map((rule) => rule.group || 'Other')))
            setChangeNote('')
        } catch (loadError: any) {
            setError(errorMessage(loadError, 'Could not load gate controls.'))
        } finally {
            setLoading(false)
        }
    }, [brand])

    useEffect(() => {
        if (open) loadRules()
    }, [open, loadRules])

    useEffect(() => {
        if (!open || !dirty) return
        const warn = (event: BeforeUnloadEvent) => {
            event.preventDefault()
            event.returnValue = ''
        }
        window.addEventListener('beforeunload', warn)
        return () => window.removeEventListener('beforeunload', warn)
    }, [dirty, open])

    const close = useCallback(() => {
        if (!dirty) {
            setOpen(false)
            return
        }
        Confirm.show(
            'Discard unsaved gate changes?',
            'Your changes have not been saved and will be lost.',
            'Discard changes',
            'Keep editing',
            () => setOpen(false)
        )
    }, [dirty])

    useEffect(() => {
        if (!open) return
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') close()
        }
        window.addEventListener('keydown', onKeyDown)
        return () => window.removeEventListener('keydown', onKeyDown)
    }, [close, open])

    const patchRule = (key: string, patch: Partial<GateRule>) => {
        setRules((current) => current.map((rule) => (rule.key === key ? { ...rule, ...patch } : rule)))
    }

    const setGroupEnabled = (group: string, enabled: boolean) => {
        setRules((current) =>
            current.map((rule) => ((rule.group || 'Other') === group ? { ...rule, enabled } : rule))
        )
    }

    const reset = () => {
        if (snapshot) setRules(cloneRules(snapshot.rules))
        setChangeNote('')
        setError(null)
    }

    const exportRules = async () => {
        if (exporting) return
        setExporting(true)
        try {
            const response = await api.post(`${BASE_URL}/api/${brand}-gate-rules`, {})
            if (!response.data?.success || !Array.isArray(response.data.rules)) {
                throw new Error(response.data?.message || 'The gate rule response was invalid')
            }
            const next: GateSnapshot = response.data
            const csv = buildGateRulesCsv({
                brandLabel,
                exportedAt: new Date().toISOString(),
                updatedAt: next.updatedAt,
                updatedBy: next.updatedBy,
                rules: next.rules,
            })
            const stamp = new Date().toISOString().slice(0, 10)
            downloadCsvFile(csv, `${brand}-gate-controls-${stamp}.csv`)
        } catch (exportError: any) {
            Report.failure(
                'Export failed',
                errorMessage(exportError, 'Could not export gate controls.'),
                'Okay'
            )
        } finally {
            setExporting(false)
        }
    }

    const save = () => {
        if (!dirty || saving) return
        const savedByKey = new Map(snapshot!.rules.map((rule) => [rule.key, rule]))
        const changed = rules.filter((rule) => {
            const saved = savedByKey.get(rule.key)
            return !saved || serialiseRules([rule]) !== serialiseRules([saved])
        })
        Confirm.show(
            `Save ${brandLabel} gate changes?`,
            `${changed.length} rule${changed.length === 1 ? '' : 's'} will change immediately for live and dry-run deliveries.`,
            'Save changes',
            'Cancel',
            async () => {
                setSaving(true)
                setError(null)
                try {
                    const response = await api.post(`${BASE_URL}/api/${brand}-gate-rules-update`, {
                        rules: changed.map(({ key, enabled, params }) => ({
                            key,
                            enabled,
                            ...(params == null ? {} : { params }),
                        })),
                        ...(changeNote.trim() ? { changeNote: changeNote.trim() } : {}),
                    })
                    if (!response.data?.success || !Array.isArray(response.data.rules)) {
                        throw new Error(response.data?.message || 'The saved gate rule response was invalid')
                    }
                    const next: GateSnapshot = response.data
                    setSnapshot({ ...next, rules: cloneRules(next.rules) })
                    setRules(cloneRules(next.rules))
                    setChangeNote('')
                    Report.success('Gate controls saved', `${brandLabel} rules are now active.`, 'Okay')
                } catch (saveError: any) {
                    setError(errorMessage(saveError, 'Could not save gate controls. Your changes are still available.'))
                } finally {
                    setSaving(false)
                }
            }
        )
    }

    if (!isAdmin) return null

    return (
        <>
            <div className={styles.gateControlsActionRow}>
                <button
                    type="button"
                    className={styles.gateControlsButton}
                    onClick={exportRules}
                    disabled={exporting}
                    title={`Download a spreadsheet of current ${brandLabel} gates, status and configuration`}
                >
                    <FaDownload aria-hidden="true" />
                    {exporting ? 'Exporting…' : 'Export gates'}
                </button>
                <button type="button" className={styles.gateControlsButton} onClick={() => setOpen(true)}>
                    <FaSlidersH aria-hidden="true" />
                    Gate controls
                </button>
            </div>
            {open && (
                <>
                    <div className={styles.slideOverBackdrop} onClick={close} />
                    <aside
                        className={`${styles.slideOver} ${styles.gateControlsSlideOver}`}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby={`${brand}-gate-controls-title`}
                    >
                        <div className={styles.slideOverHeader}>
                            <div>
                                <h4 id={`${brand}-gate-controls-title`}>{brandLabel} gate controls</h4>
                                <p>Changes apply immediately to live delivery and dry-run evaluation.</p>
                            </div>
                            <button type="button" className={styles.closeButton} onClick={close}>
                                Close
                            </button>
                        </div>

                        {loading ? (
                            <div className={styles.gateControlsState}>Loading gate controls…</div>
                        ) : error && !snapshot ? (
                            <div className={styles.gateControlsState}>
                                <p className={styles.errorText}>{error}</p>
                                <button type="button" className={styles.secondaryButton} onClick={loadRules}>
                                    Retry
                                </button>
                            </div>
                        ) : snapshot ? (
                            <div className={styles.gateControlsBody}>
                                <div className={styles.gateControlsMeta}>
                                    <span>
                                        Last updated <strong>{formatDateTime(snapshot.updatedAt)}</strong>
                                    </span>
                                    <span>
                                        by <strong>{snapshot.updatedBy || 'Unknown'}</strong>
                                    </span>
                                    <button
                                        type="button"
                                        className={styles.secondaryButton}
                                        onClick={exportRules}
                                        disabled={exporting}
                                    >
                                        {exporting ? 'Exporting…' : 'Export spreadsheet'}
                                    </button>
                                </div>
                                <input
                                    type="search"
                                    className={styles.gateControlsSearch}
                                    placeholder="Search rules, descriptions, keys or groups…"
                                    value={search}
                                    onChange={(event) => setSearch(event.target.value)}
                                />
                                {error && <p className={styles.errorText}>{error}</p>}

                                <div className={styles.gateGroups}>
                                    {groupedRules.map(([group, groupItems]) => {
                                        const isExpanded = expanded.has(group) || Boolean(search.trim())
                                        const enabledCount = groupItems.filter((rule) => rule.enabled).length
                                        return (
                                            <section className={styles.gateGroup} key={group}>
                                                <div className={styles.gateGroupHeader}>
                                                    <button
                                                        type="button"
                                                        className={styles.gateGroupToggle}
                                                        onClick={() =>
                                                            setExpanded((current) => {
                                                                const next = new Set(current)
                                                                next.has(group) ? next.delete(group) : next.add(group)
                                                                return next
                                                            })
                                                        }
                                                        aria-expanded={isExpanded}
                                                    >
                                                        {isExpanded ? <FaChevronDown /> : <FaChevronRight />}
                                                        <span>{group}</span>
                                                        <small>
                                                            {enabledCount} of {groupItems.length} enabled
                                                        </small>
                                                    </button>
                                                    <div className={styles.gateGroupActions}>
                                                        <button type="button" onClick={() => setGroupEnabled(group, true)}>
                                                            Enable all
                                                        </button>
                                                        <button type="button" onClick={() => setGroupEnabled(group, false)}>
                                                            Disable all
                                                        </button>
                                                    </div>
                                                </div>
                                                {isExpanded && (
                                                    <div className={styles.gateRuleList}>
                                                        {groupItems.map((rule) => (
                                                            <div className={styles.gateRule} key={rule.key}>
                                                                <label className={styles.gateRuleToggle}>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={rule.enabled}
                                                                        onChange={(event) =>
                                                                            patchRule(rule.key, {
                                                                                enabled: event.target.checked,
                                                                            })
                                                                        }
                                                                    />
                                                                    <span className={styles.gateSwitch} aria-hidden="true" />
                                                                    <span className={styles.gateRuleCopy}>
                                                                        <strong>{rule.label}</strong>
                                                                        {rule.description && <span>{rule.description}</span>}
                                                                        <code>{rule.key}</code>
                                                                    </span>
                                                                </label>
                                                                {rule.params && Object.keys(rule.params).length > 0 && (
                                                                    <div className={styles.gateParams}>
                                                                        {Object.entries(rule.params).map(([name, value]) => (
                                                                            <ParamInput
                                                                                key={name}
                                                                                ruleKey={rule.key}
                                                                                name={name}
                                                                                value={value}
                                                                                disabled={!rule.enabled}
                                                                                styles={styles}
                                                                                onChange={(nextValue) =>
                                                                                    patchRule(rule.key, {
                                                                                        params: {
                                                                                            ...rule.params,
                                                                                            [name]: nextValue,
                                                                                        },
                                                                                    })
                                                                                }
                                                                            />
                                                                        ))}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </section>
                                        )
                                    })}
                                    {groupedRules.length === 0 && (
                                        <p className={styles.gateControlsEmpty}>
                                            {search.trim() ? (
                                                <>
                                                    No gate rules match “{search}”. Configured values such as “28”
                                                    are not searched — try the gate name, key or group.
                                                </>
                                            ) : (
                                                'No gate rules were returned for this brand.'
                                            )}
                                        </p>
                                    )}
                                </div>

                                <label className={styles.gateChangeNote}>
                                    <span>Change note (optional)</span>
                                    <textarea
                                        rows={2}
                                        value={changeNote}
                                        maxLength={500}
                                        placeholder="Why are these gates changing?"
                                        onChange={(event) => setChangeNote(event.target.value)}
                                    />
                                </label>
                            </div>
                        ) : (
                            <div className={styles.gateControlsState}>Loading gate controls…</div>
                        )}

                        {snapshot && !loading && (
                            <div className={styles.gateControlsFooter}>
                                <span className={dirty ? styles.pendingHint : styles.appliedHint}>
                                    {dirty ? 'Unsaved changes' : 'All changes saved'}
                                </span>
                                <div>
                                    <button
                                        type="button"
                                        className={styles.secondaryButton}
                                        disabled={!dirty || saving}
                                        onClick={reset}
                                    >
                                        Reset
                                    </button>
                                    <button
                                        type="button"
                                        className={styles.applyButton}
                                        disabled={!dirty || saving}
                                        onClick={save}
                                    >
                                        {saving ? 'Saving…' : 'Save changes'}
                                    </button>
                                </div>
                            </div>
                        )}
                    </aside>
                </>
            )}
        </>
    )
}
